import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPhoneAlt, FaSyncAlt, FaPlus, FaSearch, FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { crmCallApi, crmLeadApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import { CallModal } from './components/CallModal';
import './CRM.css';

const fallbackCalls = [
  { id: 1, leadId: 1, lead: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', mobile: '9876543210', direction: 'Outbound', outcome: 'Interested', durationSeconds: 272, calledAt: '2026-10-03T09:15:00', notes: 'Interested in 50 oxygen cylinders, wants quotation' },
  { id: 2, leadId: 2, lead: 'Rajesh Kumar', company: 'Kumar Industrial Gases', mobile: '9811122233', direction: 'Outbound', outcome: 'Call Back Later', durationSeconds: 70, calledAt: '2026-10-03T09:45:00', notes: 'Asked to call after 4 PM' },
  { id: 3, leadId: 3, lead: 'Sunita Devi', company: 'Devi Gas Agency', mobile: '9765544332', direction: 'Inbound', outcome: 'Connected', durationSeconds: 382, calledAt: '2026-10-03T10:05:00', notes: 'Enquiring about quotation QT-2041 status' },
  { id: 4, leadId: 5, lead: 'Pooja Sharma', company: 'Sharma Medical Gas', mobile: '9777666555', direction: 'Outbound', outcome: 'Not Interested', durationSeconds: 45, calledAt: '2026-10-02T16:30:00', notes: 'Has existing vendor, will revisit next quarter' },
];

const fmtDuration = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60); const sec = s % 60;
  return m ? `${m}m ${sec}s` : `${sec}s`;
};
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const outcomeTone = (o) => {
  if (o === 'Interested' || o === 'Connected' || o === 'Qualified') return 'crm-badge-won';
  if (o === 'Not Interested' || o === 'Busy' || o === 'No Answer') return 'crm-badge-lost';
  if (o === 'Voicemail') return 'crm-badge-quote';
  return 'crm-badge-contacted';
};

const OUTCOME_OPTIONS = ['Connected', 'Interested', 'Not Interested', 'Call Back Later', 'Busy', 'No Answer', 'Voicemail', 'Qualified']
  .map((o) => ({ value: o, label: o }));

const DIRECTION_OPTIONS = ['Outbound', 'Inbound'].map((d) => ({ value: d, label: d }));

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMCalls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState(fallbackCalls);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [modal, setModal] = useState({ open: false, lead: null });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [all, today] = await Promise.allSettled([crmCallApi.getAll({ size: 200 }), crmCallApi.getToday()]);
      const src = today.status === 'fulfilled' && Array.isArray(today.value?.data) ? today.value.data
        : all.status === 'fulfilled' && Array.isArray(all.value?.data) ? all.value.data
        : all.status === 'fulfilled' && all.value?.data?.content ? all.value.data.content : null;
      if (src) setCalls(src);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const logCall = async (form) => {
    let saved = false;
    try {
      await crmCallApi.create({ ...form, ...(modal.lead?.id ? { leadId: modal.lead.id } : {}) });
      saved = true;
    } catch {
      if (modal.lead?.id) {
        try { await crmLeadApi.addCall(modal.lead.id, form); saved = true; } catch { saved = false; }
      }
    }
    if (!saved) throw new Error('Failed to save call');
    toast.success('Call logged.');
    setModal({ open: false, lead: null });
    fetchAll();
  };

  const filtered = calls.filter((c) => {
    if (outcomeFilter && c.outcome !== outcomeFilter) return false;
    if (directionFilter && c.direction !== directionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.lead || c.leadName || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.mobile || '').includes(q);
    }
    return true;
  });

  const stats = {
    total: calls.length,
    connected: calls.filter((c) => ['Connected', 'Interested', 'Qualified'].includes(c.outcome)).length,
    avgDuration: calls.length ? Math.round(calls.reduce((s, c) => s + (c.durationSeconds || 0), 0) / calls.length) : 0,
    todayCount: calls.filter((c) => c.calledAt && new Date(c.calledAt).toDateString() === new Date().toDateString()).length,
  };

  return (
    <div className="crm-page crm-calls-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaPhoneAlt aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">Calls</h1>
            <p className="crm-subtitle">Call log — outcomes, duration & call back tracking</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-ghost" onClick={fetchAll}><FaSyncAlt aria-hidden="true" /> Refresh</button>
          <button className="crm-btn crm-btn-primary" onClick={() => setModal({ open: true, lead: null })}><FaPlus aria-hidden="true" /> Log Call</button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {[
          { label: 'Total Calls', value: stats.total, color: '#6366F1' },
          { label: 'Connected', value: stats.connected, color: '#16A34A' },
          { label: "Today's Calls", value: stats.todayCount, color: '#0EA5E9' },
          { label: 'Avg Duration', value: fmtDuration(stats.avgDuration), color: '#F59E0B' },
        ].map((k) => (
          <div className="crm-kpi-card" key={k.label}>
            {loading ? (
              <div style={{ width: '100%' }}>
                <SkeletonBlock width="50%" height={20} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="75%" height={12} radius={6} />
              </div>
            ) : (
              <div><div className="crm-kpi-value" style={{ color: k.color }}>{k.value}</div><div className="crm-kpi-label">{k.label}</div></div>
            )}
          </div>
        ))}
      </div>

      <div className="crm-filters">
        <div className="crm-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search calls"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by lead, company, mobile..."
          />
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter calls by outcome">
          <AppDropdown
            value={outcomeFilter}
            options={OUTCOME_OPTIONS}
            onChange={setOutcomeFilter}
            placeholder="All Outcomes"
            menuWidth={220}
            menuClassName="crm-dropdown-window"
          />
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter calls by direction">
          <AppDropdown
            value={directionFilter}
            options={DIRECTION_OPTIONS}
            onChange={setDirectionFilter}
            placeholder="All Directions"
            menuWidth={200}
            menuClassName="crm-dropdown-window"
          />
        </div>
        <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => { setSearch(''); setOutcomeFilter(''); setDirectionFilter(''); }}>Clear</button>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Lead</th><th>Mobile</th><th>Direction</th><th>Outcome</th><th>Duration</th><th>Called At</th><th>Notes</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 7 }).map((__, cc) => (
                    <td key={cc}><SkeletonBlock width={cc === 0 ? 130 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td className="link-cell" onClick={() => c.leadId && navigate(`/crm/leads/${c.leadId}`)}>
                  <strong>{c.lead || c.leadName}</strong>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{c.company}</div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{c.mobile}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {c.direction === 'Inbound'
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FaArrowDown aria-hidden="true" /> Inbound</span>
                    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FaArrowUp aria-hidden="true" /> Outbound</span>}
                </td>
                <td><span className={`crm-badge ${outcomeTone(c.outcome)}`}>{c.outcome}</span></td>
                <td>{fmtDuration(c.durationSeconds)}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{fmtTime(c.calledAt)}</td>
                <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.notes}>{c.notes || '—'}</td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={7}>
                <div className="crm-empty">
                  <div className="crm-empty-icon"><FaPhoneAlt aria-hidden="true" /></div>
                  <div className="crm-empty-title">No calls found</div>
                  <div className="crm-empty-desc">Log your first call to start tracking outcomes.</div>
                </div>
              </td></tr>
            )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <CallModal open={modal.open} lead={modal.lead} onClose={() => setModal({ open: false, lead: null })} onSave={logCall} onSaved={fetchAll} />
    </div>
  );
}
